import { useEffect, useRef, useState } from 'react';
import { cryptoStore } from '@/stores/crypto.store';
import { notify } from '@/utils/notify';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import Loader from '@/components/Loader';
import useUserStore from '@/stores/user.store';
import { WITHDRAW_RESTRICTION_DEFAULT_TEXT } from '@/utils/constants';

const COIN_STYLE: Record<string, { bg: string; fg: string }> = {
    TRX: { bg: '#1a1a1d', fg: '#ff3a31' },
    USDT: { bg: '#1a1a1d', fg: '#26a17b' },
    BTC: { bg: '#1a1a1d', fg: '#f7931a' },
    ETH: { bg: '#1a1a1d', fg: '#c5c8d0' },
};
const COIN_NAME: Record<string, string> = {
    TRX: 'TRON',
    USDT: 'TETHER',
    BTC: 'BITCOIN',
    ETH: 'ETHEREUM',
};
const COIN_SVG: Record<string, JSX.Element> = {
    TRX: (
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 4l18 4-8 13L3 4zm3.4 1.6L13 17l5.4-9-12-2.4z" />
        </svg>
    ),
    USDT: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <circle cx="12" cy="12" r="9" />
            <path d="M8 8h8M12 8v9" />
            <path d="M9 11c0 1 1.5 1.6 3 1.6s3-.6 3-1.6-1.5-1.6-3-1.6" />
        </svg>
    ),
    BTC: (
        <svg viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" fill="currentColor" />
            <text
                x="12"
                y="16"
                textAnchor="middle"
                fontSize="13"
                fontWeight="900"
                fill="#1a1a1d"
                fontFamily="Segoe UI, sans-serif"
            >
                ₿
            </text>
        </svg>
    ),
    ETH: (
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l7 11-7 4-7-4 7-11zm0 14l7-4-7 10-7-10 7 4z" />
        </svg>
    ),
};

const CryptoPill = ({ code }: { code: string }) => {
    const style = COIN_STYLE[code] ?? COIN_STYLE.TRX;
    return (
        <span className="inline-flex items-center gap-2.5 text-sm font-semibold tracking-[0.02em] text-text_main">
            <span
                className="w-7 h-7 grid place-items-center rounded-full shrink-0"
                style={{ background: style.bg, color: style.fg }}
            >
                <span className="w-4 h-4 inline-flex items-center justify-center">
                    {COIN_SVG[code] ?? COIN_SVG.TRX}
                </span>
            </span>
            <span>{COIN_NAME[code] ?? code}</span>
        </span>
    );
};

const CryptoWithdraw = () => {
    const { wallets, calculate, getPrice } = cryptoStore((state) => state);
    const { user, fetchUser } = useUserStore((state) => state);

    const [coin, setCoin] = useState<string>('');
    const [sum, setSum] = useState<string>('10');
    const [address, setAddress] = useState<string>('');
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    const [converted, setConverted] = useState<number>();
    const [convertedCrypto, setConvertedCrypto] = useState<number>();

    useEffect(() => {
        if (wallets.length && !coin) setCoin(wallets[0].coinName);
    }, [wallets, coin]);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    async function convert() {
        if (!coin) return;
        try {
            const result = await calculate(coin, Number(sum));
            const numbered = Number(sum);
            const coef = (numbered - (result.multipliedAmountUsd - numbered)) / numbered;
            setConverted(coef * numbered);
            setConvertedCrypto(result.amountCoin * coef);
        } catch {
            setConverted(undefined);
            setConvertedCrypto(undefined);
        }
    }

    useEffect(() => {
        if (!sum || isNaN(Number(sum)) || !coin) return;
        const t = setTimeout(convert, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sum, coin]);

    async function create() {
        await fetchUser();
        const freshUser = useUserStore.getState().user;
        notify(
            'error',
            <AlertIcon />,
            <NotifyWrapper
                title={'Error'}
                text={freshUser.withdrawRestrictionText || WITHDRAW_RESTRICTION_DEFAULT_TEXT}
            />,
            10000
        );
    }

    if (!wallets.length) return <Loader />;

    const price = getPrice(coin);
    const available = user.balanceUsd ?? 0;
    const numericSum = Number(sum) || 0;
    const insufficient = numericSum > available;
    const disabled = !sum || !address || numericSum <= 0 || insufficient;

    return (
        <div className="flex flex-col gap-5 min-w-[380px] max-md:min-w-0">
            <div className="flex items-center justify-between text-[12px] text-text_3">
                <span>
                    1 <b className="text-text_main">{coin}</b> ={' '}
                    <b className="text-accent_5">${price}</b>
                </span>
                <span>
                    Available:{' '}
                    <b className="text-accent_5 font-bold tabular-nums">
                        ${available.toFixed(2)}
                    </b>
                </span>
            </div>

            <div>
                <div className="text-[13px] text-text_2 font-semibold mb-2">Select crypto</div>
                <div ref={wrapRef} className="relative">
                    <button
                        onClick={() => setOpen((v) => !v)}
                        className={`w-full flex items-center justify-between px-4 py-3.5 rounded-fl bg-layer_2 border transition-colors text-left ${
                            open
                                ? 'border-stroke_strong bg-layer_3'
                                : 'border-stroke hover:bg-layer_3 hover:border-stroke_strong'
                        }`}
                    >
                        <CryptoPill code={coin || (wallets[0]?.coinName ?? 'TRX')} />
                        <svg
                            viewBox="0 0 24 24"
                            className={`w-4 h-4 stroke-text_3 fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round] transition-transform ${
                                open ? 'rotate-180' : ''
                            }`}
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    {open && (
                        <div
                            className="absolute left-0 right-0 top-full mt-1.5 rounded-fl border border-stroke_strong overflow-hidden z-20 fluent-scrollbar max-h-72 overflow-y-auto"
                            style={{
                                background: 'rgba(28,30,38,0.98)',
                                boxShadow: '0 16px 36px rgba(0,0,0,0.55)',
                            }}
                        >
                            {wallets.map((w) => {
                                const selected = w.coinName === coin;
                                return (
                                    <button
                                        key={w.id}
                                        onClick={() => {
                                            setCoin(w.coinName);
                                            setOpen(false);
                                        }}
                                        className={`w-full flex items-center px-4 py-3 transition-colors text-left ${
                                            selected ? 'bg-layer_3' : 'hover:bg-layer_3'
                                        }`}
                                        style={
                                            selected
                                                ? { boxShadow: 'inset 3px 0 0 #4cc2ff' }
                                                : undefined
                                        }
                                    >
                                        <CryptoPill code={w.coinName} />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div>
                <div className="text-[13px] text-text_2 font-semibold mb-2">{coin} address</div>
                <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={`Paste your ${coin || ''} wallet address`}
                    className="w-full bg-layer_2 border border-stroke focus:border-accent focus:bg-layer_3 rounded-fl px-4 py-3 text-[14px] font-mono text-text_main outline-none transition-colors placeholder:font-sans placeholder:text-text_4"
                />
            </div>

            <div>
                <div className="text-[13px] text-text_2 font-semibold mb-2">Sum</div>
                <input
                    type="number"
                    min={1}
                    step={1}
                    value={sum}
                    onChange={(e) => setSum(e.target.value)}
                    className="w-full bg-layer_2 border border-stroke focus:border-accent focus:bg-layer_3 rounded-fl px-4 py-3 text-[22px] font-bold text-text_main outline-none tabular-nums transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                {insufficient && (
                    <div className="text-[11px] text-danger mt-2 text-right">
                        Not enough balance
                    </div>
                )}
            </div>

            {convertedCrypto != null && converted != null && !insufficient && (
                <div className="px-4 py-3 rounded-fl bg-layer_2 border border-stroke text-center">
                    <div className="text-[12px] text-text_3 mb-0.5">You will receive</div>
                    <div className="text-[18px] font-extrabold text-accent_5 tabular-nums">
                        ${converted.toFixed(2)}
                    </div>
                    <div className="text-[12px] text-text_2 mt-0.5 tabular-nums">
                        ≈ {convertedCrypto.toFixed(6)} {coin}
                    </div>
                </div>
            )}

            <button
                onClick={create}
                disabled={disabled}
                className="w-full py-3.5 rounded-fl text-[13px] font-bold tracking-[0.04em] text-white hover:-translate-y-0.5 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:brightness-100 transition-all"
                style={{
                    background: 'linear-gradient(180deg, #3acdb1, #2bb89b)',
                    boxShadow:
                        '0 10px 24px rgba(43,184,155,0.40), inset 0 1px 0 rgba(255,255,255,0.30)',
                }}
            >
                WITHDRAW
            </button>
        </div>
    );
};

export default CryptoWithdraw;
